<script lang="ts">
    import { cn, getClient, getHighlightSegments } from "../../lib/utils.js";
    import { m } from "../../lib/messages.svelte.js";
    import { Spinner } from "../../lib/components/ui/spinner";
    import { Badge } from "../../lib/components/ui/badge";
    import { Input } from "../../lib/components/ui/input";
    import * as Select from "../../lib/components/ui/select/index.js";
    import * as Table from "../../lib/components/ui/table/index.js";
    import TablePagination from "../../lib/components/common/table-pagination.svelte";
    import * as Sheet from "../../lib/components/ui/sheet/index.js";
    import ChevronUp from "@lucide/svelte/icons/chevron-up";
    import ChevronDown from "@lucide/svelte/icons/chevron-down";
    import ChevronsUpDown from "@lucide/svelte/icons/chevrons-up-down";
    import Bot from "@lucide/svelte/icons/bot";
    import type { Res } from "@repo/backend/client";
    import { maybeFormatDate } from "../../lib/components/salesorder/utils/date-utils.js";
    import TransactionDetailSlideout from "./transaction-detail-slideout.svelte";

    interface Props {
        from: string;
        to: string;
    }

    const { from, to }: Props = $props();

    type AdjustmentType = Res["Get dashboard transactions"]["rows"][number]["adjustments"][number];
    type Case = Res["Get dashboard transactions"]["rows"][number];
    type SortColumn =
        | "emailSubject"
        | "isSubmitted"
        | "customerName"
        | "suggestedPositionCount"
        | "submittedPositionCount"
        | "orderReference"
        | "erpTransactionNumber"
        | "processedAt"
        | "submittedAt"
        | "submittedBy";

    const adjustmentTypes: AdjustmentType[] = [
        "CUSTOMER_CHANGED",
        "QUANTITY_CHANGED",
        "PRODUCT_CHANGED",
        "POSITION_ADDED",
        "POSITION_REMOVED",
        "PRODUCT_NOT_DETECTED",
    ];

    const pageSizeOptions = [10, 25, 50, 100] as const;
    let pageSize = $state<(typeof pageSizeOptions)[number]>(25);
    let pageNumber = $state(1);
    let searchInput = $state("");
    let searchQuery = $state("");
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;
    let statusFilter = $state<"all" | "submitted" | "auto-submitted" | "pending">("all");
    let adjustmentFilters = $state<AdjustmentType[]>([]);
    let sortColumn = $state<SortColumn | null>(null);
    let sortDirection = $state<"asc" | "desc" | null>(null);
    let selectedCase = $state<Case | null>(null);
    let sheetOpen = $state(false);

    function openCaseDetail(c: Case) {
        selectedCase = c;
        sheetOpen = true;
    }

    $effect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const currentInput = searchInput;
        searchTimeout = setTimeout(() => {
            searchQuery = currentInput;
            pageNumber = 1;
        }, 400);
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    });

    async function fetchCases(
        from_: typeof from,
        to_: typeof to,
        page_: typeof pageNumber,
        pageSize_: typeof pageSize,
        searchQuery_: typeof searchQuery,
        statusFilter_: typeof statusFilter,
        adjustmentFilters_: typeof adjustmentFilters,
        sortColumn_: typeof sortColumn,
        sortDirection_: typeof sortDirection,
    ) {
        return await getClient()["Get dashboard transactions"]({
            jsonBody: {
                from: from_,
                to: to_,
                page: page_,
                pageSize: pageSize_,
                searchQuery: searchQuery_.trim() || undefined,
                statusFilter: statusFilter_,
                adjustments: adjustmentFilters_.length > 0 ? adjustmentFilters_ : undefined,
                sortColumn: sortColumn_ ?? undefined,
                sortDirection: sortDirection_ ?? undefined,
            },
        });
    }
    let promise = $derived(
        fetchCases(
            from,
            to,
            pageNumber,
            pageSize,
            searchQuery,
            statusFilter,
            adjustmentFilters,
            sortColumn,
            sortDirection,
        ),
    );
    $effect(() => {
        const id = setInterval(() => {
            promise = fetchCases(
                from,
                to,
                pageNumber,
                pageSize,
                searchQuery,
                statusFilter,
                adjustmentFilters,
                sortColumn,
                sortDirection,
            );
        }, 120_000);
        return () => {
            clearInterval(id);
        };
    });

    function handleSort(column: SortColumn) {
        if (sortColumn === column) {
            if (sortDirection === "asc") {
                sortDirection = "desc";
            } else if (sortDirection === "desc") {
                sortColumn = null;
                sortDirection = null;
            }
        } else {
            sortColumn = column;
            sortDirection = "asc";
        }
        pageNumber = 1;
    }

    function toggleAdjustmentFilter(adj: AdjustmentType) {
        if (adjustmentFilters.includes(adj)) {
            adjustmentFilters = adjustmentFilters.filter(a => a !== adj);
        } else {
            adjustmentFilters = [...adjustmentFilters, adj];
        }
        pageNumber = 1;
    }
</script>

<div class="flex flex-col gap-4 overflow-hidden py-4">
    <div class="flex flex-wrap items-center gap-6">
        <div class="flex flex-col gap-1">
            <span class="text-muted-foreground text-sm">{m["dashboard.cases.search"]()}</span>
            <Input
                type="search"
                placeholder={m["dashboard.cases.searchPlaceholder"]()}
                class="w-64"
                bind:value={searchInput}
            />
        </div>
        <div class="flex flex-col gap-1">
            <span class="text-muted-foreground text-sm">{m["dashboard.cases.statusFilter"]()}</span>
            <Select.Root
                type="single"
                value={statusFilter}
                onValueChange={v => {
                    statusFilter = v as "all" | "submitted" | "auto-submitted" | "pending";
                    pageNumber = 1;
                }}
            >
                <Select.Trigger class="w-35">
                    {statusFilter === "all"
                        ? m["dashboard.cases.statusAll"]()
                        : statusFilter === "submitted"
                          ? m["dashboard.cases.statusSubmitted"]()
                          : statusFilter === "auto-submitted"
                            ? m["dashboard.cases.statusAutoSubmitted"]()
                            : m["dashboard.cases.statusPending"]()}
                </Select.Trigger>
                <Select.Content>
                    <Select.Item value="all">{m["dashboard.cases.statusAll"]()}</Select.Item>
                    <Select.Item value="submitted">{m["dashboard.cases.statusSubmitted"]()}</Select.Item>
                    <Select.Item value="auto-submitted">{m["dashboard.cases.statusAutoSubmitted"]()}</Select.Item>
                    <Select.Item value="pending">{m["dashboard.cases.statusPending"]()}</Select.Item>
                </Select.Content>
            </Select.Root>
        </div>
        <div class="flex flex-col gap-1">
            <span class="text-muted-foreground text-sm">{m["dashboard.cases.adjustmentsFilter"]()}</span>
            <div class="flex h-9 flex-wrap items-center gap-1">
                {#each adjustmentTypes as adj (adj)}
                    <button
                        type="button"
                        class={cn(
                            "h-7 rounded-md border px-2 text-xs transition-colors",
                            adjustmentFilters.includes(adj)
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-input bg-background hover:bg-accent",
                        )}
                        onclick={() => toggleAdjustmentFilter(adj)}
                    >
                        {m[`dashboard.cases.adjustment.${adj}`]()}
                    </button>
                {/each}
            </div>
        </div>
    </div>

    {#await promise}
        <div class="flex flex-1 items-center justify-center">
            <Spinner class="size-8" />
            <span class="text-muted-foreground ml-2">{m["dashboard.cases.loading"]()}</span>
        </div>
    {:then data}
        <div class="flex-1 overflow-auto">
            <Table.Root>
                <Table.Header class="bg-background sticky top-0 z-10">
                    <Table.Row data-header-row>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("emailSubject")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.emailSubject"]()}
                                {@render sortIcon("emailSubject")}
                            </span>
                        </Table.Head>
                        <Table.Head class="min-w-42.5">
                            {m["dashboard.cases.columns.adjustments"]()}
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("isSubmitted")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.status"]()}
                                {@render sortIcon("isSubmitted")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("customerName")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.customer"]()}
                                {@render sortIcon("customerName")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("suggestedPositionCount")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.suggestedPositions"]()}
                                {@render sortIcon("suggestedPositionCount")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("submittedPositionCount")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.submittedPositions"]()}
                                {@render sortIcon("submittedPositionCount")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("orderReference")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.orderReference"]()}
                                {@render sortIcon("orderReference")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("erpTransactionNumber")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.erpTransactionNumber"]()}
                                {@render sortIcon("erpTransactionNumber")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("processedAt")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.processedAt"]()}
                                {@render sortIcon("processedAt")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("submittedAt")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.submittedAt"]()}
                                {@render sortIcon("submittedAt")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("submittedBy")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.submittedBy"]()}
                                {@render sortIcon("submittedBy")}
                            </span>
                        </Table.Head>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {#each data.rows as row (row.suggestedTransactionId)}
                        <Table.Row class="cursor-pointer" onclick={() => openCaseDetail(row)}>
                            <Table.Cell class="max-w-64">
                                <span class="block truncate" title={row.emailSubject ?? ""}>
                                    {@render highlightText(row.emailSubject, searchQuery)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                <div class="flex flex-wrap gap-1">
                                    {#each row.adjustments as adj (adj)}
                                        <Badge class="bg-orange-500 text-white hover:bg-orange-500">
                                            {m[`dashboard.cases.adjustment.${adj}`]()}
                                        </Badge>
                                    {/each}
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <Badge
                                    variant="outline"
                                    class={row.isSubmitted
                                        ? "border-success-foreground text-success-foreground"
                                        : "border-warning-foreground text-warning-foreground"}
                                >
                                    {#if row.isAutoSubmitted}
                                        <Bot class="mr-1 size-3" />
                                        {m["dashboard.cases.status.autoSubmitted"]()}
                                    {:else if row.isSubmitted}
                                        {m["dashboard.cases.status.submitted"]()}
                                    {:else}
                                        {m["dashboard.cases.status.pending"]()}
                                    {/if}
                                </Badge>
                            </Table.Cell>
                            <Table.Cell class="max-w-48">
                                <span class="block truncate" title={row.customerName ?? ""}>
                                    {@render highlightText(row.customerName, searchQuery)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                {row.suggestedPositionCount}
                            </Table.Cell>
                            <Table.Cell>
                                {row.submittedPositionCount ?? "-"}
                            </Table.Cell>
                            <Table.Cell class="max-w-40">
                                <span class="block truncate" title={row.orderReference ?? ""}>
                                    {@render highlightText(row.orderReference, searchQuery)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                {@render highlightText(row.erpTransactionNumber, searchQuery)}
                            </Table.Cell>
                            <Table.Cell>
                                {maybeFormatDate(row.processedAt, true) ?? "-"}
                            </Table.Cell>
                            <Table.Cell>
                                {maybeFormatDate(row.submittedAt, true) ?? "-"}
                            </Table.Cell>
                            <Table.Cell>
                                {#if row.isAutoSubmitted}
                                    <span class="inline-flex items-center gap-1">
                                        <Bot class="size-4" />
                                        {m["assistantSalesOrder.submissionStatus.turianAi"]()}
                                    </span>
                                {:else}
                                    {@render highlightText(row.submittedBy, searchQuery)}
                                {/if}
                            </Table.Cell>
                        </Table.Row>
                    {:else}
                        <Table.Row>
                            <Table.Cell colspan={11} class="h-24 text-center">
                                {m["dashboard.cases.noResults"]()}
                            </Table.Cell>
                        </Table.Row>
                    {/each}
                </Table.Body>
            </Table.Root>
        </div>

        <TablePagination
            {pageSizeOptions}
            {pageSize}
            {pageNumber}
            totalCount={data.totalCount}
            onPageSizeChange={size => {
                pageSize = size as (typeof pageSizeOptions)[number];
                pageNumber = 1;
            }}
            onPageChange={p => {
                pageNumber = p;
            }}
        />
    {:catch e}
        <div class="text-destructive flex flex-1 items-center justify-center">
            {e.message ?? m["dashboard.cases.loadingError"]()}
        </div>
    {/await}
</div>

<Sheet.Root bind:open={sheetOpen}>
    <Sheet.Content
        class="w-[80vw] max-w-none data-[state=open]:duration-200 sm:max-w-none"
        side="right"
        hideCloseButton
    >
        {#if selectedCase}
            <TransactionDetailSlideout transaction={selectedCase} />
        {/if}
    </Sheet.Content>
</Sheet.Root>

{#snippet highlightText(text: string | null | undefined, query: string)}
    {#each getHighlightSegments(text, query) as segment (segment.index)}
        {#if segment.highlight}
            <mark class="bg-yellow-200">{segment.text}</mark>
        {:else}
            {segment.text}
        {/if}
    {/each}
{/snippet}

{#snippet sortIcon(column: SortColumn)}
    {#if sortColumn === column && sortDirection === "asc"}
        <ChevronUp class="size-4 shrink-0" />
    {:else if sortColumn === column && sortDirection === "desc"}
        <ChevronDown class="size-4 shrink-0" />
    {:else}
        <ChevronsUpDown class="text-muted-foreground size-4 shrink-0" />
    {/if}
{/snippet}

<style>
    :global([data-header-row]:hover > th) {
        background-color: transparent !important;
    }
    :global([data-header-row] > th[data-sortable]:hover) {
        background-color: var(--muted) !important;
    }
</style>
